import * as React from 'react';
import { formatBytes, formatDuration, shortUrl } from '../lib/format';
import { lsUsage } from '../lib/storage';
import { HistoryEntry } from '../types';

const { useEffect, useRef, useState } = React;

interface Props {
  entries: HistoryEntry[];
  activeIdx: number | null;
  currentBaseUrl: string;
  onLoad: (entry: HistoryEntry, idx: number) => void;
  onReplayCurrent: (entry: HistoryEntry, idx: number) => void;
  onReplaySaved: (entry: HistoryEntry, idx: number) => void;
  onCopyCurl: (entry: HistoryEntry) => void;
  onDelete: (idx: number) => void;
  onClear: () => void;
}

interface CtxMenu {
  x: number; y: number;
  entry: HistoryEntry;
  idx: number;
}

export default function History(props: Props) {
  const [menu, setMenu] = useState<CtxMenu | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menu) return;
    const hide = () => setMenu(null);
    document.addEventListener('click', hide);
    document.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      document.removeEventListener('click', hide);
      document.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [menu]);

  return (
    <>
      <section>
        <h2>
          History
          <span className="muted" style={{ fontWeight: 'normal', textTransform: 'none', letterSpacing: 0, marginLeft: 'auto', marginRight: 8 }}>
            {props.entries.length} entries · {formatBytes(lsUsage())}
          </span>
          <button className="tiny" onClick={props.onClear}>clear</button>
        </h2>
        <p className="muted">Click any item to reload form, options, and route. Right-click for more actions.</p>
        <ul className="history">
          {props.entries.map((e, idx) => {
            const mode = e.mode || 'directions';
            const badge = mode === 'matrix' ? '📊' : '📍';
            const pts = mode === 'matrix'
              ? ((e.body as any).locations?.length || 0)
              : ((e.body as any).coordinates?.length || 0);
            const pref = (e.body as any).preference || (mode === 'matrix' ? 'matrix' : '?');
            let sum: string;
            if (!e.ok) sum = `ERROR: ${e.error || 'unknown'}`;
            else if (mode === 'matrix') {
              const n = e.summary?.n ?? ((e.body as any).locations?.length ?? '?');
              sum = `${n}×${n} matrix · ${e.summary?.ms || 0}ms`;
            } else {
              sum = `${e.summary?.km} km · ${formatDuration(e.summary?.duration)} · ${e.summary?.ms}ms`;
            }
            const cls = [
              !e.ok ? 'error' : '',
              props.activeIdx === idx ? 'active' : '',
            ].filter(Boolean).join(' ');
            return (
              <li
                key={idx}
                className={cls}
                title="Click to load. Right-click for more actions."
                onClick={() => props.onLoad(e, idx)}
                onContextMenu={(ev) => {
                  ev.preventDefault();
                  setMenu({ x: ev.clientX, y: ev.clientY, entry: e, idx });
                }}
              >
                <div className="when">
                  {new Date(e.at).toLocaleString()} · {badge} <b>{e.profile}</b> · {pref} · {pts} pts
                  {e.ok && e.response && <span className="replay-hint">↻ replay</span>}
                </div>
                <div className="summary">{sum}</div>
                {e.baseUrl && <div className="url" title={e.baseUrl}>{shortUrl(e.baseUrl)}</div>}
              </li>
            );
          })}
        </ul>
      </section>

      {menu && (
        <div
          ref={menuRef}
          className="ctx-menu"
          style={{ left: menu.x, top: menu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="ctx-item" onClick={() => { props.onReplayCurrent(menu.entry, menu.idx); setMenu(null); }}>
            ▶ Replay with current URL
            <span className="ctx-sub">{shortUrl(props.currentBaseUrl) || '(empty)'}</span>
          </div>
          {menu.entry.baseUrl && (
            <div
              className={`ctx-item${menu.entry.baseUrl === props.currentBaseUrl ? ' muted' : ''}`}
              onClick={() => { props.onReplaySaved(menu.entry, menu.idx); setMenu(null); }}
            >
              ▶ Replay with saved URL
              <span className="ctx-sub">{shortUrl(menu.entry.baseUrl)}</span>
            </div>
          )}
          <div className="ctx-sep" />
          <div className="ctx-item" onClick={() => { props.onLoad(menu.entry, menu.idx); setMenu(null); }}>
            📥 Load only (no send)
          </div>
          <div className="ctx-item" onClick={() => { props.onCopyCurl(menu.entry); setMenu(null); }}>
            📋 Copy as cURL
          </div>
          <div className="ctx-sep" />
          <div className="ctx-item danger" onClick={() => { props.onDelete(menu.idx); setMenu(null); }}>
            🗑 Delete entry
          </div>
        </div>
      )}
    </>
  );
}

